import React, { useState, useRef } from "react";
import { formatNumber } from "../../../../shared/utils/numberFormatter";
import { cryptiqueAnalyticsTheme as theme } from "../theme";
import { makeFormatValue, getThresholdValues } from "../utils/axisFormat";

const DEFAULT_STEP_COLOR = "#B39DFF";

export default function FunnelVisualization({ 
  steps = [], 
  measuring = 'conversion_rate',
  overallConversionRate = null,
  breakdowns = [], // New prop for breakdown data
  config,          // Chart / axis configuration (from editor)
}) {
  const [hoveredLightArea, setHoveredLightArea] = useState(null); // { stepIndex, x, y }
  const [hoveredDarkArea, setHoveredDarkArea] = useState(null);  // { stepIndex, x, y }
  const [hoveredSegment, setHoveredSegment] = useState(null); // { stepIndex, breakdownIndex, kind: 'light' | 'dark', x, y }
  const stepRefs = useRef({});
  const darkAreaRefs = useRef({});
  const segmentRefs = useRef({});
  const leftY = config?.axis?.leftY ?? {};
  const rightY = config?.axis?.rightY ?? {};
  const xAxis = config?.axis?.xAxis ?? {};
  const funnelAxis = config?.funnelAxis ?? "left"; // "left" | "right" - which side the Y-axis is on
  const yAxisConfig = funnelAxis === "right" ? rightY : leftY;
  const formatYValue = makeFormatValue(yAxisConfig);
  const decimalsConfig = yAxisConfig.decimals ?? ".0";
  const percentDecimalPlaces = decimalsConfig === ".00" ? 2 : 1;
  const formatPercent = (value) => {
    const num = Number(value) || 0;
    return `${num.toFixed(percentDecimalPlaces)}%`;
  };
  const leftYVisible = yAxisConfig.visible !== false;
  const leftYLabel = yAxisConfig.label;
  const xAxisVisible = xAxis.visible !== false;
  const xAxisLabel = xAxis.label || "";
  const outOfBounds = yAxisConfig.outOfBounds ?? "hidden"; // "hidden" = use threshold bounds (cropped view); "visible" = show entire chart, no bounds
  const { min: minThreshold, max: maxThreshold } = getThresholdValues(yAxisConfig);
  const hasThresholds = minThreshold != null || maxThreshold != null;
  // Apply threshold bounds when Out of Bounds = Hidden. When Visible, show full chart.
  const effectiveHasThresholds = hasThresholds && outOfBounds === "hidden";
  const rangeMin = minThreshold ?? 0;
  const rangeMax = maxThreshold ?? 100;
  const rangeSpan = rangeMax - rangeMin;
  // Explicit: only "log" string enables logarithmic Y-axis; everything else is linear
  const yScale = (yAxisConfig && String(yAxisConfig.scale) === "log") ? "log" : "linear";
  const stepColors = config?.colors && Array.isArray(config.colors) ? config.colors : [];
  const getStepColor = (index) => stepColors[index % stepColors.length] || DEFAULT_STEP_COLOR;

  // Log scale: base-10; cannot show 0, use 0.1% as floor. Axis domain for ticks and bar mapping.
  const LOG_FLOOR_PCT = 0.1;
  const axisMinLog = yScale === "log" ? (effectiveHasThresholds ? Math.max(LOG_FLOOR_PCT, rangeMin) : LOG_FLOOR_PCT) : 0;
  const axisMaxLog = yScale === "log" ? (effectiveHasThresholds ? rangeMax : 100) : 100;
  const logSpan = axisMaxLog > axisMinLog && axisMinLog > 0 ? Math.log10(axisMaxLog) - Math.log10(axisMinLog) : 1;

  /** Map actual % value to display height % for log scale. Linear scale uses raw %. */
  const valueToHeightPct = (valuePct) => {
    if (yScale !== "log" || valuePct <= 0) return valuePct;
    const v = Math.max(valuePct, axisMinLog);
    return ((Math.log10(v) - Math.log10(axisMinLog)) / logSpan) * 100;
  };

  // Helper to format time interval
  const formatTimeInterval = (interval) => {
    if (!interval) return 'N/A';
    if (typeof interval === 'string') return interval;
    if (typeof interval === 'object' && interval !== null) {
      const { hours = 0, minutes = 0, seconds = 0 } = interval;
      const parts = [];
      if (hours > 0) parts.push(`${hours}h`);
      if (minutes > 0) parts.push(`${minutes}m`);
      if (seconds > 0 && parts.length === 0) parts.push(`${seconds}s`);
      return parts.length > 0 ? parts.join(' ') : '0s';
    }
    return String(interval);
  };

  // Check if we have breakdowns
  const hasBreakdowns = breakdowns && breakdowns.length > 0;

  // Generate colors for breakdown segments
  const getBreakdownColors = () => {
    const colors = [
      '#7c3aed', '#3b82f6', '#10b981', '#f59e0b', 
      '#ef4444', '#8b5cf6', '#06b6d4', '#84cc16',
      '#f97316', '#ec4899', '#6366f1', '#14b8a6',
      '#a855f7', '#eab308', '#22c55e', '#06b6d4'
    ];
    return colors;
  };

  const breakdownColors = getBreakdownColors();

  // Get first step value for scaling (all heights relative to this)
  // For conversion_rate, use count; for others, use value
  const getFirstStepValue = () => {
    if (measuring === 'conversion_rate') {
      return steps.length > 0 ? (steps[0]?.count || 0) : 100;
    } else {
      // For unique_users, total_conversions, total_sessions, use value
      return steps.length > 0 ? (steps[0]?.value || steps[0]?.count || 0) : 100;
    }
  };
  
  const firstStepValue = getFirstStepValue();

  // Get max value across all breakdowns for step 1 (for scaling)
  const getMaxBreakdownStep1Value = () => {
    if (!hasBreakdowns) return firstStepValue;
    
    let maxValue = 0;
    breakdowns.forEach(breakdown => {
      const step1Data = breakdown.steps?.step1;
      if (step1Data) {
        const value = measuring === 'conversion_rate' 
          ? (step1Data.count || 0)
          : (step1Data.value || step1Data.count || 0);
        maxValue = Math.max(maxValue, value);
      }
    });
    
    return maxValue > 0 ? maxValue : firstStepValue;
  };

  const maxBreakdownStep1Value = getMaxBreakdownStep1Value();

  // Calculate heights for a breakdown segment at a specific step
  const getBreakdownHeights = (breakdown, stepIndex) => {
    const getStepValue = (stepData) => {
      if (measuring === 'conversion_rate') {
        return stepData?.count || 0;
      } else {
        return stepData?.value || stepData?.count || 0;
      }
    };

    const stepNum = stepIndex + 1;
    const currentStepData = breakdown.steps?.[`step${stepNum}`] || {};
    const currentValue = getStepValue(currentStepData);
    
    let prevValue = 0;
    if (stepIndex === 0) {
      // For step 1, use the step1 count
      prevValue = getStepValue(breakdown.steps?.step1 || {});
    } else {
      // For subsequent steps, use previous step's value
      const prevStepData = breakdown.steps?.[`step${stepIndex}`] || {};
      prevValue = getStepValue(prevStepData);
    }

    // Scale heights relative to max breakdown step1 value
    const lightHeight = maxBreakdownStep1Value > 0 
      ? (prevValue / maxBreakdownStep1Value) * 100 
      : 0;
    
    const darkHeight = maxBreakdownStep1Value > 0 
      ? (currentValue / maxBreakdownStep1Value) * 100 
      : 0;

    return {
      lightHeight: Math.min(lightHeight, 100),
      darkHeight: Math.min(darkHeight, 100),
      prevValue,
      currentValue,
      conversionRate: currentStepData.conversionRate || (stepIndex === 0 ? 100 : 0)
    };
  };

  // Calculate heights using Mixpanel's logic
  const getHeights = (step, index) => {
    // For conversion_rate, use count; for others, use value
    const getStepValue = (s) => {
      if (measuring === 'conversion_rate') {
        return s.count || 0;
      } else {
        return s.value || s.count || 0;
      }
    };
    
    const currentValue = getStepValue(step);
    const prevValue = index === 0 
      ? firstStepValue 
      : getStepValue(steps[index - 1]);
    
    // Light background = previous step value (scaled to first step)
    const lightHeight = firstStepValue > 0 
      ? (prevValue / firstStepValue) * 100 
      : 0;
    
    // Dark fill = current step value (scaled to first step)
    const darkHeight = firstStepValue > 0 
      ? (currentValue / firstStepValue) * 100 
      : 0;
    
    return {
      lightHeight: Math.min(lightHeight, 100),
      darkHeight: Math.min(darkHeight, 100),
      prevValue,
      currentValue
    };
  };

  // Get display value for breakdown segment
  const getBreakdownDisplayValue = (breakdown, stepIndex) => {
    const stepNum = stepIndex + 1;
    const stepData = breakdown.steps?.[`step${stepNum}`] || {};
    const count = stepData.count || 0;
    const rawValue = stepData.value;
    const stepMeasuring = stepData.measuring || measuring;
    const conversionRate =
      typeof stepData.conversionRate === "number"
        ? stepData.conversionRate
        : stepIndex === 0
        ? 100
        : 0;

    let primaryValueDisplay;

    switch (stepMeasuring) {
      case "unique_users":
        primaryValueDisplay = formatNumber(rawValue || count);
        break;
      case "total_conversions":
      case "total_sessions":
      case "property_sum":
        primaryValueDisplay = formatNumber(rawValue || 0);
        break;
      case "time_to_convert": {
        const intervalVal = rawValue || stepData.timeToConvert;
        primaryValueDisplay = formatTimeInterval(intervalVal);
        break;
      }
      case "conversion_rate":
      default:
        primaryValueDisplay = formatNumber(count);
        break;
    }

    return {
      percentage: `${conversionRate.toFixed(2)}%`,
      count: primaryValueDisplay
    };
  };

  // Get display value for step (badge shows step-to-step conversion)
  const getDisplayValue = (step, measuring) => {
    const count = step.count || 0;
    const conversion = step.conversion || 0; // Step-to-step conversion from backend
    const fmt = formatYValue;
    switch (measuring) {
      case 'conversion_rate':
        return { percentage: formatPercent(conversion), count: fmt(count) };
      case 'unique_users':
        return { percentage: formatPercent(conversion), count: fmt(step.value || count) };
      case 'total_conversions':
        return { percentage: formatPercent(conversion), count: fmt(step.value || 0) };
      case 'total_sessions':
        return { percentage: formatPercent(conversion), count: fmt(step.value || 0) };
      case 'time_to_convert':
        const timeStr = formatTimeInterval(step.value || step.timeToConvert);
        return { percentage: formatPercent(conversion), count: timeStr };
      case 'property_sum':
        return { percentage: formatPercent(conversion), count: fmt(step.value || 0) };
      default:
        return { percentage: `${conversion.toFixed(2)}%`, count: formatNumber(count) };
    }
  };

  // Tooltip for breakdown segment (converted)
  const getBreakdownTooltip = (breakdown, stepIndex) => {
    const stepNum = stepIndex + 1;
    const stepData = breakdown.steps?.[`step${stepNum}`] || {};
    const count = stepData.count || 0;
    const conversionRate = stepData.conversionRate || (stepIndex === 0 ? 100 : 0);
    const breakdownName = breakdown.breakdownValue || breakdown.breakdownKey || 'Unknown';

    return [
      breakdownName,
      `${conversionRate.toFixed(4)}% converted`,
      `${formatNumber(count)} total times`
    ];
  };

  // Tooltip for breakdown segment drop-off (light area)
  const getBreakdownDropoffTooltip = (breakdown, stepIndex) => {
    const breakdownName = breakdown.breakdownValue || breakdown.breakdownKey || 'Unknown';
    const stepNum = stepIndex + 1;

    const current = breakdown.steps?.[`step${stepNum}`]?.count || 0;
    let prev;

    if (stepIndex === 0) {
      // For first step, use step1 as both prev and current so drop-off is 0
      prev = breakdown.steps?.step1?.count || 0;
    } else {
      prev = breakdown.steps?.[`step${stepIndex}`]?.count || 0;
    }

    const dropOffValue = Math.max(prev - current, 0);
    const dropOffPercent = prev > 0 ? (dropOffValue / prev) * 100 : 0;

    return [
      breakdownName,
      `${dropOffPercent.toFixed(4)}% drop-off`,
      `${formatNumber(dropOffValue)} total times`
    ];
  };

  // Tooltip for light area (drop-off)
  const getLightAreaTooltip = (step, index, prevValue) => {
    const getStepValue = (s) => {
      if (measuring === 'conversion_rate') {
        return s.count || 0;
      } else {
        return s.value || s.count || 0;
      }
    };
    
    const currentValue = getStepValue(step);
    const dropOffValue = Math.max(prevValue - currentValue, 0);
    const dropOffPercent = prevValue > 0 ? ((dropOffValue / prevValue) * 100) : 0;

    return [
      "Overall",
      `${formatYValue(dropOffPercent)} drop-off`,
      `${formatYValue(dropOffValue)} total times`
    ];
  };

  // Tooltip for dark area (converted)
  const getDarkAreaTooltip = (step, index) => {
    const stepMeasuring = measuring;
    const count = step.count || 0;
    const value = step.value || 0;
    const conversion = step.conversion || 0;

    let tooltipLines = [];

    switch (stepMeasuring) {
      case 'conversion_rate':
        tooltipLines = [
          "Overall",
          `${formatYValue(conversion)} converted`,
          `${formatYValue(count)} total times`
        ];
        break;
      case 'unique_users':
        tooltipLines = [
          "Overall",
          `${formatYValue(value || count)} unique users`,
          `${formatYValue(count)} total times`
        ];
        break;
      case 'total_conversions':
        tooltipLines = [
          "Overall",
          `${formatYValue(value)} total conversions`,
          `${formatYValue(count)} total times`
        ];
        break;
      case 'total_sessions':
        tooltipLines = [
          "Overall",
          `${formatYValue(value)} sessions`,
          `${formatYValue(count)} total times`
        ];
        break;
      case 'time_to_convert':
        const timeStr = formatTimeInterval(value || step.timeToConvert);
        tooltipLines = [
          "Overall",
          `${timeStr} average time to convert`,
          `${formatYValue(count)} total times`
        ];
        break;
      case 'property_sum':
        tooltipLines = [
          "Overall",
          `${formatYValue(value)} property sum`,
          `${formatYValue(count)} total times`
        ];
        break;
      default:
        tooltipLines = [
          "Overall",
          `${formatYValue(count)} total times`
        ];
    }

    return tooltipLines;
  };

  // Calculate overall conversion rate (first step to last step)
  const overallConv = overallConversionRate !== null 
    ? overallConversionRate 
    : (steps.length > 1 && steps[0]?.count > 0
      ? ((steps[steps.length - 1]?.count || 0) / steps[0].count) * 100
      : (steps.length === 1 ? 100 : 0));
  
  // Use overall conversion from backend if available
  const finalOverallConv = steps.length > 0 && steps[0]?.overallConversion !== undefined
    ? steps[0].overallConversion
    : overallConv;

  // Y-axis ticks: linear = equal spacing (0, 25, 50, 75, 100); log = base-10 spacing (0.1%, 1%, 10%, 100% or log-spaced in narrow range)
  const getYAxisTicks = () => {
    if (measuring === 'conversion_rate' && yScale === 'log') {
      const logMin = Math.log10(Math.max(axisMinLog, 0.01));
      const logMax = Math.log10(axisMaxLog);
      const span = logMax - logMin;
      // Full range (0.1–100): use powers of 10 so axis clearly looks logarithmic
      if (axisMinLog <= 0.5 && axisMaxLog >= 50) {
        const powerTicks = [0.1, 1, 10, 100].filter((t) => t >= axisMinLog && t <= axisMaxLog);
        return powerTicks.length >= 2 ? powerTicks : [axisMinLog, axisMaxLog];
      }
      // Narrow range: 5 log-spaced ticks (ends = axis min/max) so spacing is visibly unequal
      return Array.from({ length: 5 }, (_, i) => {
        if (i === 0) return axisMinLog;
        if (i === 4) return axisMaxLog;
        const v = Math.pow(10, logMin + (span * i) / 4);
        return Math.round(v * 100) / 100;
      });
    }
    if (measuring === 'conversion_rate' && effectiveHasThresholds && rangeSpan > 0) {
      return [rangeMin, rangeMin + rangeSpan * 0.25, rangeMin + rangeSpan * 0.5, rangeMin + rangeSpan * 0.75, rangeMax];
    }
    if (measuring === 'conversion_rate') {
      return [0, 25, 50, 75, 100];
    } else {
      // For other measuring types (unique_users, total_conversions, total_sessions), show numbers
      const tickCount = 5;
      const ticks = [];
      const maxValue = hasBreakdowns ? maxBreakdownStep1Value : firstStepValue;
      for (let i = 0; i <= tickCount; i++) {
        const value = (maxValue / tickCount) * i;
        ticks.push(Math.round(value));
      }
      return ticks;
    }
  };

  const yAxisTicks = getYAxisTicks();
  // Tick position from top of visible area (0 = bottom, 100 = top)
  const getTickTopPercent = (tick) => {
    if (measuring === 'conversion_rate' && yScale === 'log') {
      const t = Math.max(tick, axisMinLog);
      return 100 - ((Math.log10(t) - Math.log10(axisMinLog)) / logSpan) * 100;
    }
    if (measuring === 'conversion_rate' && effectiveHasThresholds && rangeSpan > 0) {
      return 100 - ((tick - rangeMin) / rangeSpan) * 100;
    }
    return measuring === 'conversion_rate' ? (100 - tick) : (100 - (firstStepValue > 0 ? (tick / firstStepValue) * 100 : 0));
  };

  const containerHeight = 280;
  // Linear + thresholds: crop plot to range. Log: full height; axis range is axisMinLog–axisMaxLog.
  const plotHeight = yScale === "log" ? containerHeight : (effectiveHasThresholds && rangeSpan > 0 ? (rangeSpan / 100) * containerHeight : containerHeight);
  const cropOffsetBottom = yScale === "log" ? 0 : (effectiveHasThresholds && rangeSpan > 0 ? (rangeMin / 100) * containerHeight : 0);

  // Handle light area hover (drop-off) - anchor to top-right of container
  const handleLightAreaHover = (index, event) => {
    const containerRef = stepRefs.current[index];
    if (containerRef) {
      const rect = containerRef.getBoundingClientRect();
      setHoveredLightArea({
        stepIndex: index,
        x: rect.right - 12, // Top-right of container
        y: rect.top + 12
      });
    }
  };

  // Handle dark area hover (converted) - anchor to top-right of dark block
  const handleDarkAreaHover = (index, event) => {
    const darkRef = darkAreaRefs.current[index];
    if (darkRef) {
      const rect = darkRef.getBoundingClientRect();
      setHoveredDarkArea({
        stepIndex: index,
        x: rect.right - 12, // Top-right of dark block
        y: rect.top + 12
      });
    }
  };

  // Handle breakdown segment hover
  const handleBreakdownHover = (stepIndex, breakdownIndex, kind, event) => {
    const refKey = `${stepIndex}-${breakdownIndex}`;
    const segmentRef = segmentRefs.current[refKey];
    if (segmentRef) {
      const rect = segmentRef.getBoundingClientRect();
      setHoveredSegment({
        stepIndex,
        breakdownIndex,
        kind,
        x: rect.right - 12,
        y: rect.top + 12
      });
    }
  };

  // Render breakdown mode (multiple bars per step)
  if (hasBreakdowns) {
    return (
      <div className="w-full">
        {/* Breakdown Legend */}
        <div className="mb-4 flex flex-wrap items-center gap-4">
          {breakdowns.map((breakdown, index) => {
            const breakdownName = breakdown.breakdownValue || breakdown.breakdownKey || 'Unknown';
            const overallConv = breakdown.overallConversion || 0;
            const color = breakdownColors[index % breakdownColors.length];
            
            return (
              <div key={index} className="flex items-center gap-2">
                <div 
                  className="w-3 h-3 rounded"
                  style={{ backgroundColor: color }}
                />
                <span className="text-sm" style={{ color: theme.textPrimary }}>
                  {breakdownName}
                </span>
                <span className="text-sm" style={{ color: theme.textMuted }}>
                  • {overallConv.toFixed(2)}%
                </span>
              </div>
            );
          })}
        </div>

        {/* Funnel Chart with Breakdown Bars */}
        <div className="relative mb-8">
          {/* Y-Axis Label (beyond the axis, only when leftYVisible) */}
          {leftYVisible && leftYLabel && (
            <div
              className="absolute top-1/2 -rotate-90 origin-center text-xs"
              style={{
                color: theme.textMuted,
                whiteSpace: "nowrap",
                transform: funnelAxis === "right"
                  ? "translateX(100%) translateY(-50%) rotate(90deg)"
                  : "translateX(-100%) translateY(-50%) rotate(-90deg)",
                transformOrigin: "center",
                [funnelAxis === "right" ? "right" : "left"]: "-8px",
              }}
            >
              {leftYLabel}
            </div>
          )}
          {/* Y-Axis (only when leftYVisible) */}
          {leftYVisible && (
            <div className={`absolute top-0 bottom-0 w-12 ${funnelAxis === "right" ? "right-0 pl-2" : "left-0 pr-2"}`}>
              {yAxisTicks.map((tick, i) => {
                const tickPosition =
                  measuring === "conversion_rate"
                    ? tick
                    : maxBreakdownStep1Value > 0
                    ? (tick / maxBreakdownStep1Value) * 100
                    : 0;

                return (
                  <div
                    key={i}
                    className={`absolute text-xs ${funnelAxis === "right" ? "text-left" : "text-right"}`}
                    style={{
                      top: `${100 - tickPosition}%`,
                      color: theme.textMuted,
                      transform: "translateY(-50%)",
                    }}
                  >
                    {formatYValue(tick)}
                  </div>
                );
              })}
            </div>
          )}

          {/* Grid Lines (only when leftYVisible) */}
          {leftYVisible && (
            <div className={`absolute top-0 bottom-0 ${funnelAxis === "right" ? "left-0 right-14" : "left-14 right-0"}`}>
              {yAxisTicks.map((tick, i) => {
                const tickPosition =
                  measuring === "conversion_rate"
                    ? tick
                    : maxBreakdownStep1Value > 0
                    ? (tick / maxBreakdownStep1Value) * 100
                    : 0;

                return (
                  <div
                    key={i}
                    className="absolute left-0 right-0 border-t"
                    style={{
                      top: `${100 - tickPosition}%`,
                      borderColor: theme.border,
                      opacity: 0.3,
                    }}
                  />
                );
              })}
            </div>
          )}

          {/* Chart Area - Multiple bars per step */}
          <div 
            className="flex items-end"
            style={{ 
              height: `${containerHeight}px`,
              ...(leftYVisible
                ? funnelAxis === "right"
                  ? { marginRight: leftYLabel ? "96px" : "56px" }
                  : { marginLeft: leftYLabel ? "96px" : "56px" }
                : {})
            }}
          >
            {steps.map((step, stepIndex) => {
              const stepLabel = step.label || step.eventName || `Step ${stepIndex + 1}`;
              
              return (
                <div
                  key={stepIndex}
                  className="relative flex-1 h-full"
                  style={{
                    borderRight: stepIndex < steps.length - 1 ? `1px solid ${theme.border}` : 'none'
                  }}
                >
                  {/* Render bars for each breakdown segment */}
                  <div className="relative w-full h-full flex gap-1">
                    {breakdowns.map((breakdown, breakdownIndex) => {
                      const { lightHeight, darkHeight, conversionRate } = getBreakdownHeights(breakdown, stepIndex);
                      const display = getBreakdownDisplayValue(breakdown, stepIndex);
                      const color = breakdownColors[breakdownIndex % breakdownColors.length];
                      const isHovered = hoveredSegment?.stepIndex === stepIndex && 
                                        hoveredSegment?.breakdownIndex === breakdownIndex;
                      const refKey = `${stepIndex}-${breakdownIndex}`;

                      return (
                        <div
                          key={breakdownIndex}
                          ref={(el) => { segmentRefs.current[refKey] = el; }}
                          className="relative h-full flex-1"
                        >
                          {/* Light background (previous step) - only for step > 0 */}
                          {stepIndex > 0 && (
                            <div 
                              className="absolute bottom-0 left-0 right-0 transition-all cursor-pointer"
                              onMouseEnter={(e) => handleBreakdownHover(stepIndex, breakdownIndex, 'light', e)}
                              onMouseLeave={() => setHoveredSegment(null)}
                              style={{ 
                                height: `${lightHeight}%`,
                                backgroundColor: color,
                                opacity: isHovered && hoveredSegment?.kind === 'light' ? 0.35 : 0.25
                              }}
                            />
                          )}

                          {/* Dark fill (current step) */}
                          <div
                            className="absolute bottom-0 left-0 right-0 transition-all cursor-pointer"
                            onMouseEnter={(e) => handleBreakdownHover(stepIndex, breakdownIndex, 'dark', e)}
                            onMouseLeave={() => setHoveredSegment(null)}
                            style={{
                              height: `${darkHeight}%`,
                              background: color,
                              opacity: isHovered && hoveredSegment?.kind === 'dark' ? 0.98 : 1,
                              boxShadow: isHovered && hoveredSegment?.kind === 'dark'
                                ? `0 4px 12px ${color}40`
                                : `0 2px 4px ${color}25`
                            }}
                          >
                            {/* Tag at top of bar */}
                            {darkHeight > 0 && (
                              <div 
                                className="absolute left-1/2"
                                style={{ 
                                  top: '8px',
                                  transform: 'translateX(-50%)',
                                  pointerEvents: 'none'
                                }}
                              >
                                <div
                                  className="bg-white rounded px-2 py-1 text-center shadow-sm"
                                  style={{
                                    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.12)',
                                    minWidth: '60px'
                                  }}
                                >
                                  <div 
                                    className="text-xs font-medium leading-tight"
                                    style={{ 
                                      color: '#1f2937',
                                      fontFamily: 'system-ui, -apple-system, sans-serif'
                                    }}
                                  >
                                    {display.percentage}
                                  </div>
                                  <div 
                                    className="text-xs font-medium leading-tight"
                                    style={{ 
                                      color: '#1f2937',
                                      fontFamily: 'system-ui, -apple-system, sans-serif'
                                    }}
                                  >
                                    {display.count}
                                  </div>
                                </div>
                              </div>
                            )}
                          </div>

                          {/* Tooltip */}
                          {isHovered && hoveredSegment?.x !== undefined && hoveredSegment?.y !== undefined && (
                            <div
                              className="fixed z-50 bg-white border pointer-events-none"
                              style={{
                                left: `${hoveredSegment.x}px`,
                                top: `${hoveredSegment.y}px`,
                                transform: 'translate(-100%, 0)',
                                padding: '12px',
                                minWidth: '200px',
                                maxWidth: '300px',
                                borderColor: theme.border,
                                borderLeftWidth: '3px',
                                borderLeftColor: color,
                                boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                                borderRadius: '8px'
                              }}
                            >
                              {(hoveredSegment.kind === 'light'
                                ? getBreakdownDropoffTooltip(breakdown, stepIndex)
                                : getBreakdownTooltip(breakdown, stepIndex)
                              ).map((line, i, arr) => (
                                <div 
                                  key={i}
                                  className="text-sm"
                                  style={{ 
                                    color: i === 0 ? theme.textPrimary : theme.textSecondary,
                                    fontWeight: i === 0 ? 600 : 400,
                                    marginBottom: i < arr.length - 1 ? '4px' : '0'
                                  }}
                                >
                                  {line}
                                </div>
                              ))}
                              <div
                                className="absolute"
                                style={{
                                  top: '100%',
                                  right: '12px',
                                  transform: 'translateY(-50%) rotate(45deg)',
                                  width: '12px',
                                  height: '12px',
                                  backgroundColor: 'white',
                                  borderRight: `1px solid ${theme.border}`,
                                  borderBottom: `1px solid ${theme.border}`
                                }}
                              />
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>

                  {/* Step label at bottom (X-Axis) */}
                  {xAxisVisible && (
                    <div className="absolute -bottom-6 left-0 right-0 text-center">
                      <span 
                        className="text-sm"
                        style={{ 
                          color: theme.textPrimary,
                          maxWidth: '100%',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          display: 'inline-block'
                        }}
                      >
                        <span style={{ color: theme.textMuted, marginRight: '4px' }}>
                          {stepIndex + 1}
                        </span>
                        <span style={{ color: theme.textPrimary }}>
                          {stepLabel}
                        </span>
                      </span>
                    </div>
                  )}
                  {/* X-Axis Label (if provided) */}
                  {xAxisVisible && xAxisLabel && stepIndex === steps.length - 1 && (
                    <div className="absolute -bottom-12 left-0 right-0 text-center">
                      <span className="text-xs" style={{ color: theme.textMuted }}>
                        {xAxisLabel}
                      </span>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  }

  // Original non-breakdown rendering
  return (
    <div className="w-full">
      {/* Overall Conversion Label - Centered */}
      <div className="mb-4 flex justify-center">
        <span className="text-sm font-medium" style={{ color: theme.textPrimary }}>
          Overall • {finalOverallConv.toFixed(2)}%
        </span>
      </div>

      {/* Funnel Chart - Connected Step Containers */}
      <div className="relative mb-8">
        {/* Y-Axis Label (beyond the axis, only when leftYVisible) */}
        {leftYVisible && leftYLabel && (
          <div
            className="absolute top-1/2 -rotate-90 origin-center text-xs"
            style={{
              color: theme.textMuted,
              whiteSpace: "nowrap",
              transform: funnelAxis === "right"
                ? "translateX(100%) translateY(-50%) rotate(90deg)"
                : "translateX(-100%) translateY(-50%) rotate(-90deg)",
              transformOrigin: "center",
              [funnelAxis === "right" ? "right" : "left"]: "-8px",
            }}
          >
            {leftYLabel}
          </div>
        )}
        {/* Y-Axis with ticks (only when leftYVisible) */}
        {leftYVisible && (
          <div className={`absolute top-0 bottom-0 w-12 ${funnelAxis === "right" ? "right-0 pl-2" : "left-0 pr-2"}`}>
            {yAxisTicks.map((tick, i) => {
              // For conversion_rate, tick is already a percentage (0-100)
              // For other types, calculate position based on firstStepValue
              const tickPosition =
                measuring === "conversion_rate"
                  ? tick
                  : firstStepValue > 0
                  ? (tick / firstStepValue) * 100
                  : 0;

              return (
                <div
                  key={i}
                  className={`absolute text-xs ${funnelAxis === "right" ? "text-left" : "text-right"}`}
                  style={{
                    top: `${100 - tickPosition}%`,
                    color: theme.textMuted,
                    transform: "translateY(-50%)",
                  }}
                >
                  {formatYValue(tick)}
                </div>
              );
            })}
          </div>
        )}

        {/* Grid Lines (only when leftYVisible) */}
        {leftYVisible && (
          <div className={`absolute top-0 bottom-0 ${funnelAxis === "right" ? "left-0 right-14" : "left-14 right-0"}`}>
            {yAxisTicks.map((tick, i) => {
              // For conversion_rate, tick is already a percentage (0-100)
              // For other types, calculate position based on firstStepValue
              const tickPosition =
                measuring === "conversion_rate"
                  ? tick
                  : firstStepValue > 0
                  ? (tick / firstStepValue) * 100
                  : 0;

              return (
                <div
                  key={i}
                  className="absolute left-0 right-0 border-t"
                  style={{
                    top: `${100 - tickPosition}%`,
                    borderColor: theme.border,
                    opacity: 0.3,
                  }}
                />
              );
            })}
          </div>
        )}

        {/* Chart Area - Connected Step Containers */}
        <div 
          className="flex items-end"
          style={{ 
            height: `${containerHeight}px`,
            ...(leftYVisible
              ? funnelAxis === "right"
                ? { marginRight: leftYLabel ? "96px" : "56px" }
                : { marginLeft: leftYLabel ? "96px" : "56px" }
              : {})
          }}
        >
          {steps.map((step, index) => {
            const { lightHeight, darkHeight, prevValue } = getHeights(step, index);
            const display = getDisplayValue(step, measuring);
            const isFirst = index === 0;
            const isLast = index === steps.length - 1;
            const isLightHovered = hoveredLightArea?.stepIndex === index;
            const isDarkHovered = hoveredDarkArea?.stepIndex === index;

            return (
              <div
                key={step.id || index}
                ref={(el) => { stepRefs.current[index] = el; }}
                className="relative flex-1 h-full"
                style={{
                  borderRight: isLast ? 'none' : `1px solid ${theme.border}`,
                  borderLeft: isFirst ? 'none' : 'none'
                }}
              >
                {/* Light Background (Previous Step Position) - hoverable only for steps > 0 */}
                {index > 0 && (
                  <div 
                    className="absolute bottom-0 left-0 right-0 transition-all cursor-pointer"
                    onMouseEnter={(e) => handleLightAreaHover(index, e)}
                    onMouseLeave={() => setHoveredLightArea(null)}
                    style={{ 
                      height: `${lightHeight}%`,
                      backgroundColor: isLightHovered
                        ? 'rgba(179, 157, 255, 0.35)'
                        : 'rgba(179, 157, 255, 0.25)',
                      opacity: isLightHovered ? 0.5 : 0.4,
                      borderRadius: isFirst && lightHeight === 100 ? '4px 0 0 4px' : 
                                 isLast && lightHeight === 100 ? '0 4px 4px 0' :
                                 isFirst ? '4px 0 0 0' :
                                 isLast ? '0 4px 0 0' : '0'
                    }}
                  />
                )}

                {/* Dark Fill (Current Step Position) */}
                <div
                  ref={(el) => { darkAreaRefs.current[index] = el; }}
                  className="absolute bottom-0 left-0 right-0 transition-all cursor-pointer"
                  onMouseEnter={(e) => handleDarkAreaHover(index, e)}
                  onMouseLeave={() => setHoveredDarkArea(null)}
                  style={{
                    height: `${darkHeight}%`,
                    background: '#B39DFF',
                    opacity: isDarkHovered ? 0.98 : 1,
                    borderRadius: isFirst && darkHeight === 100 ? '4px 0 0 4px' : 
                                 isLast && darkHeight === 100 ? '0 4px 4px 0' :
                                 isFirst ? '4px 0 0 0' :
                                 isLast ? '0 4px 0 0' : '0',
                    boxShadow: isDarkHovered 
                      ? '0 4px 12px rgba(179, 157, 255, 0.4)'
                      : '0 2px 4px rgba(179, 157, 255, 0.25)'
                  }}
                >
                  {/* Tag at Top of Dark Area (like Mixpanel) */}
                  {darkHeight > 0 && (
                    <div 
                      className="absolute left-1/2"
                      style={{ 
                        top: '8px',
                        transform: 'translateX(-50%)',
                        pointerEvents: 'none'
                      }}
                    >
                      <div
                        className="bg-white rounded px-2 py-1 text-center shadow-sm"
                        style={{
                          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.12)',
                          minWidth: '60px'
                        }}
                      >
                        {/* Percentage */}
                        <div 
                          className="text-xs font-medium leading-tight"
                          style={{ 
                            color: '#1f2937',
                            fontFamily: 'system-ui, -apple-system, sans-serif'
                          }}
                        >
                          {display.percentage}
                        </div>
                        {/* Count */}
                        <div 
                          className="text-xs font-medium leading-tight"
                          style={{ 
                            color: '#1f2937',
                            fontFamily: 'system-ui, -apple-system, sans-serif'
                          }}
                        >
                          {display.count}
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Step Number and Event Name at Bottom (X-Axis) */}
                {xAxisVisible && (
                  <div className="absolute -bottom-6 left-0 right-0 text-center">
                    <span 
                      className="text-sm"
                      style={{ 
                        color: theme.textPrimary,
                        maxWidth: '100%',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        display: 'inline-block'
                      }}
                    >
                      <span style={{ color: theme.textMuted, marginRight: '4px' }}>
                        {index + 1}
                      </span>
                      <span style={{ color: theme.textPrimary }}>
                        {step.label || step.eventName || `Step ${index + 1}`}
                      </span>
                    </span>
                  </div>
                )}
                {/* X-Axis Label (if provided) */}
                {xAxisVisible && xAxisLabel && index === steps.length - 1 && (
                  <div className="absolute -bottom-12 left-0 right-0 text-center">
                    <span className="text-xs" style={{ color: theme.textMuted }}>
                      {xAxisLabel}
                    </span>
                  </div>
                )}

                {/* Tooltip for Light Area (drop-off) - rendered outside container with fixed positioning */}
                {isLightHovered && index > 0 && hoveredLightArea.x !== undefined && hoveredLightArea.y !== undefined && (
                  <div
                    className="fixed z-50 bg-white border pointer-events-none"
                    style={{
                      left: `${hoveredLightArea.x}px`,
                      top: `${hoveredLightArea.y}px`,
                      transform: 'translate(-100%, 0)',
                      padding: '12px',
                      minWidth: '200px',
                      maxWidth: '300px',
                      borderColor: theme.border,
                      borderLeftWidth: '3px',
                      borderLeftColor: '#B39DFF',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                      borderRadius: '8px'
                    }}
                  >
                    {getLightAreaTooltip(step, index, prevValue).map((line, i) => (
                      <div 
                        key={i}
                        className="text-sm"
                        style={{ 
                          color: i === 0 ? theme.textPrimary : theme.textSecondary,
                          fontWeight: i === 0 ? 600 : 400,
                          marginBottom: i < getLightAreaTooltip(step, index, prevValue).length - 1 ? '4px' : '0'
                        }}
                      >
                        {line}
                      </div>
                    ))}
                    {/* Tooltip Arrow - pointing down and right */}
                    <div
                      className="absolute"
                      style={{
                        top: '100%',
                        right: '12px',
                        transform: 'translateY(-50%) rotate(45deg)',
                        width: '12px',
                        height: '12px',
                        backgroundColor: 'white',
                        borderRight: `1px solid ${theme.border}`,
                        borderBottom: `1px solid ${theme.border}`
                      }}
                    />
                  </div>
                )}

                {/* Tooltip for Dark Area (converted) - rendered outside container with fixed positioning */}
                {isDarkHovered && hoveredDarkArea.x !== undefined && hoveredDarkArea.y !== undefined && (
                  <div
                    className="fixed z-50 bg-white border pointer-events-none"
                    style={{
                      left: `${hoveredDarkArea.x}px`,
                      top: `${hoveredDarkArea.y}px`,
                      transform: 'translate(-100%, 0)',
                      padding: '12px',
                      minWidth: '200px',
                      maxWidth: '300px',
                      borderColor: theme.border,
                      borderLeftWidth: '3px',
                      borderLeftColor: '#B39DFF',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                      borderRadius: '8px'
                    }}
                  >
                    {getDarkAreaTooltip(step, index).map((line, i) => (
                      <div 
                        key={i}
                        className="text-sm"
                        style={{ 
                          color: i === 0 ? theme.textPrimary : theme.textSecondary,
                          fontWeight: i === 0 ? 600 : 400,
                          marginBottom: i < getDarkAreaTooltip(step, index).length - 1 ? '4px' : '0'
                        }}
                      >
                        {line}
                      </div>
                    ))}
                    {/* Tooltip Arrow - pointing down and right */}
                    <div
                      className="absolute"
                      style={{
                        top: '100%',
                        right: '12px',
                        transform: 'translateY(-50%) rotate(45deg)',
                        width: '12px',
                        height: '12px',
                        backgroundColor: 'white',
                        borderRight: `1px solid ${theme.border}`,
                        borderBottom: `1px solid ${theme.border}`
                      }}
                    />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
